import { aitrasApi } from '@/state/api/aitrasApi';
import type {
  FactorDescriptor,
  AssessmentUploadInitRequest,
  AssessmentUploadInitResponse,
  AssessmentAcceptedResponse,
  AssessmentStatusResponse,
  FeedbackRequest,
} from '@/types';

/**
 * Assessment endpoints. The presigned two-phase upload the SPA uses:
 * `initUpload` → multipart S3 POST (see services/storageUpload) → `completeUpload`
 * → poll `getAssessment` until COMPLETED/FAILED (see hooks/useAssessmentPolling).
 */
export const assessmentApi = aitrasApi.injectEndpoints({
  endpoints: (builder) => ({
    /** GET /v1/factors — tenant-enabled provided + custom factors. */
    getFactors: builder.query<FactorDescriptor[], void>({
      query: () => '/factors',
      providesTags: ['Factors'],
    }),

    /** POST /v1/assessments/uploads — phase 1: create row + presigned multipart POST. */
    initUpload: builder.mutation<AssessmentUploadInitResponse, AssessmentUploadInitRequest>({
      query: (body) => ({ url: '/assessments/uploads', method: 'POST', body }),
    }),

    /** POST /v1/assessments/{id}/complete — phase 2: confirm upload, enqueue job. */
    completeUpload: builder.mutation<AssessmentAcceptedResponse, string>({
      query: (id) => ({ url: `/assessments/${id}/complete`, method: 'POST' }),
    }),

    /** GET /v1/assessments/{id} — status + terminal payloads. Driven by the polling hook. */
    getAssessment: builder.query<AssessmentStatusResponse, string>({
      query: (id) => `/assessments/${id}`,
      providesTags: (_result, _err, id) => [{ type: 'Assessment', id }],
    }),

    /** POST /v1/assessments/{id}/feedback — only valid once COMPLETED. */
    submitFeedback: builder.mutation<void, { id: string; body: FeedbackRequest }>({
      query: ({ id, body }) => ({ url: `/assessments/${id}/feedback`, method: 'POST', body }),
    }),
  }),
});

export const {
  useGetFactorsQuery,
  useInitUploadMutation,
  useCompleteUploadMutation,
  useLazyGetAssessmentQuery,
  useSubmitFeedbackMutation,
} = assessmentApi;
